import type { PreparedSpatialSegment } from "../../components/overlay/types";
import { quantizePresentationTime } from "../../utils/time";
import type { TrackingSelectionIndex } from "./selection";

export type CoverageRange = {
  start: number;
  end: number;
};

/** Displayable box ranges keyed by the original timeline cluster's interaction ID. */
export type BoxCoverage = ReadonlyMap<string, readonly CoverageRange[]>;

function firstLaterAnchor(anchors: readonly PreparedSpatialSegment[], time: number): number {
  let low = 0;
  let high = anchors.length;
  while (low < high) {
    const middle = (low + high) >>> 1;
    if (anchors[middle]!.startTime <= time) low = middle + 1;
    else high = middle;
  }
  return low;
}

interface PredictionRange {
  kind: "prediction";
  source: PreparedSpatialSegment;
  intervalStart: number;
  order: number;
  newerGtAt: number;
}

type ActiveRange = PredictionRange | { kind: "raw"; source: PreparedSpatialSegment };
type Boundary = { time: number; range?: ActiveRange; entering?: boolean };

/** Selection gives the latest GT interval priority, breaking ties by original interval order. */
const hasPriority = (a: PredictionRange, b: PredictionRange) =>
  a.intervalStart > b.intervalStart || (a.intervalStart === b.intervalStart && a.order > b.order);

// A lazy-removal heap keeps long, overlapping VFR intervals from turning the sweep quadratic.
class PredictionHeap {
  private values: PredictionRange[] = [];

  add(value: PredictionRange) {
    let index = this.values.push(value) - 1;
    while (index > 0) {
      const parent = (index - 1) >>> 1;
      if (!hasPriority(value, this.values[parent]!)) break;
      this.values[index] = this.values[parent]!;
      index = parent;
    }
    this.values[index] = value;
  }

  current(active: ReadonlySet<PredictionRange>): PredictionRange | undefined {
    while (this.values[0] && !active.has(this.values[0])) {
      const last = this.values.pop()!;
      if (!this.values.length) break;
      let index = 0;
      while (index * 2 + 1 < this.values.length) {
        let child = index * 2 + 1;
        if (child + 1 < this.values.length && hasPriority(this.values[child + 1]!, this.values[child]!)) child++;
        if (!hasPriority(this.values[child]!, last)) break;
        this.values[index] = this.values[child]!;
        index = child;
      }
      this.values[index] = last;
    }
    return this.values[0];
  }
}

/** Project raw GT and accepted decoded frames once per selection-index revision, never per playback tick. */
export function buildBoxCoverage(index: TrackingSelectionIndex, durationSeconds: number): BoxCoverage {
  const duration = Number.isFinite(durationSeconds) && durationSeconds > 0 ? durationSeconds : Infinity;
  const coverage = new Map<string, CoverageRange[]>();
  const tracks = new Map<string, Boundary[]>();
  const clusterId = (segment: PreparedSpatialSegment): string | undefined =>
    segment.type === "point_box" && index.anchorTracks.has(segment.id)
      ? (segment.interactionId ?? segment.id)
      : undefined;

  for (const segment of index.byId.values()) {
    const id = clusterId(segment);
    if (id !== undefined) coverage.set(id, []);
  }
  const add = (range: ActiveRange, start: number, end: number) => {
    const segment = range.source;
    const id = clusterId(segment);
    // A GT outside the playable source cannot supply a displayable endpoint frame.
    if (id === undefined || segment.startTime >= duration || !Number.isFinite(start) || !Number.isFinite(end)) return;
    start = Math.min(duration, Math.max(0, start));
    end = Math.min(duration, Math.max(0, end));
    if (!(start < end)) return;
    const trackId = index.anchorTracks.get(segment.id)!;
    const boundaries = tracks.get(trackId) ?? [];
    boundaries.push({ time: start, range, entering: true }, { time: end, range, entering: false });
    if (range.kind === "prediction" && range.newerGtAt > start && range.newerGtAt < end) {
      boundaries.push({ time: range.newerGtAt });
    }
    tracks.set(trackId, boundaries);
  };

  // The selection index already includes each raw GT's explicit/displayUntil cutoff.
  for (const { start, end, value } of index.raw.ranges) add({ kind: "raw", source: value.segment }, start, end);

  for (const [trackId, { ranges }] of index.intervalsByTrack) {
    const anchors = index.anchorsByTrack.get(trackId) ?? [];
    for (const { value } of ranges) {
      const { interval, result, order } = value;
      for (let i = 0; i < result.frames.length; i++) {
        const frame = result.frames[i]!;
        if (frame.method === "unavailable") continue;
        // Match selection's endpoint precedence, including a one-frame interval.
        const anchor = i === 0 ? interval.start : i === result.frames.length - 1 ? interval.end : interval.start;
        const source = index.byId.get(anchor.id);
        if (!source) continue;
        // A later frame supersedes this record even if that later frame is rejected.
        const end = Math.min(
          quantizePresentationTime(frame.timestamp + frame.duration),
          quantizePresentationTime(result.frames[i + 1]?.timestamp ?? Infinity),
        );
        add(
          {
            kind: "prediction",
            source,
            intervalStart: interval.start.time,
            order,
            newerGtAt: anchors[firstLaterAnchor(anchors, anchor.time)]?.startTime ?? Infinity,
          },
          quantizePresentationTime(frame.timestamp),
          end,
        );
      }
    }
  }

  for (const [trackId, boundaries] of tracks) {
    const anchors = index.anchorsByTrack.get(trackId) ?? [];
    // Even a zero-length raw window can change which GT suppresses the winner.
    for (const anchor of anchors) {
      if (Number.isFinite(anchor.startTime) && anchor.startTime >= 0 && anchor.startTime < duration) {
        boundaries.push({ time: anchor.startTime });
      }
    }
    boundaries.sort((a, b) => a.time - b.time);
    const raw = new Set<PreparedSpatialSegment>();
    const active = new Set<PredictionRange>();
    const predictions = new PredictionHeap();
    let position = 0;
    while (position < boundaries.length) {
      const start = boundaries[position]!.time;
      do {
        const { range, entering } = boundaries[position++]!;
        if (range?.kind === "raw") {
          if (entering) raw.add(range.source);
          else raw.delete(range.source);
        } else if (range) {
          if (entering) {
            active.add(range);
            predictions.add(range);
          } else active.delete(range);
        }
      } while (position < boundaries.length && boundaries[position]!.time === start);
      const end = boundaries[position]?.time;
      if (end === undefined) break;
      const append = (source: PreparedSpatialSegment) => coverage.get(clusterId(source)!)!.push({ start, end });
      const winner = predictions.current(active);
      if (winner && start < winner.newerGtAt) append(winner.source);
      else if (winner) {
        // Keep the blocked winner active: removing it would incorrectly revive an older interval.
        const latest = anchors[firstLaterAnchor(anchors, start) - 1];
        if (latest && raw.has(latest)) append(latest);
        else raw.forEach(append);
      } else raw.forEach(append);
    }
  }

  for (const [id, ranges] of coverage) {
    ranges.sort((a, b) => a.start - b.start || a.end - b.end);
    const merged: CoverageRange[] = [];
    for (const range of ranges) {
      const previous = merged.at(-1);
      if (previous && quantizePresentationTime(range.start) <= quantizePresentationTime(previous.end)) {
        previous.end = Math.max(previous.end, range.end);
      } else merged.push({ ...range });
    }
    coverage.set(id, merged);
  }
  return coverage;
}
